/**
 * The operator's working state on the cost table.
 *
 * Reopening the table rebuilt every option from the saved rates with hardcoded
 * defaults (profitMargin 10, custom 0, extra 10, selectBox false) and an empty
 * screening selection — so the pricing work done before sending options to a
 * customer was silently discarded, and had to be retyped from memory to
 * nominate the option the customer had just chosen.
 *
 * These are the fields the operator sets by hand and nothing else derives, so
 * they are the ones worth persisting. Rates themselves are NOT stored here:
 * those live on the saved airline/agent and must stay authoritative, or a stale
 * draft would quietly resurrect an old price.
 */


/**
 * The amount a screening choice is worth, derived from the CURRENT rates.
 *
 * Mirrors how the tiles compute their own figures. The draft stores which
 * screening the operator chose, never the price — restoring a saved amount
 * would let an old figure re-enter a live quote after rates moved. So the
 * choice is theirs and the number is always today's.
 */
export function screeningValueFor(option, screeningPrice, type, cumulativeVolumeWeight) {
  const sp = screeningPrice || {};
  const CW = num(cumulativeVolumeWeight ?? option?.cumulativeVolumeWeight);

  if (type === "PTS") {
    return num(
      sp.primary ??
        (sp.primaryScreeningPrice?.ratePerVolumeWeight
          ? sp.primaryScreeningPrice.ratePerVolumeWeight * CW
          : sp.primaryScreeningPrice?.minCharge ?? 0)
    );
  }
  if (type === "STS") {
    return num(
      sp.secondary ??
        (sp.secondaryScreeningPrice?.ratePerVolumeWeight
          ? sp.secondaryScreeningPrice.ratePerVolumeWeight * CW
          : sp.secondaryScreeningPrice?.minCharge ?? 0)
    );
  }
  if (type === "PAS") {
    return num(
      option?.airline?.screeningPrices?.primary?.ratePerVolumeWeight ??
        option?.airline?.screeningPrices?.primary ??
        0
    );
  }
  if (type === "SAS") {
    return num(
      option?.airline?.screeningPrices?.secondary?.ratePerVolumeWeight ??
        option?.airline?.screeningPrices?.secondary ??
        0
    );
  }
  return 0;
}

/** Stable identity of a priced row — the same key the table uses for screening. */
export function draftRowKey(option, screeningPrice) {
  const airline = option?.airline?.airlineCode || "";
  const agent = option?.agentCode || "";
  const dest = screeningPrice?.destinationAirport || "";
  if (!airline && !agent && !dest) return "";
  return `${airline}_${agent}_${dest}`;
}

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Collect the working state out of the live table.
 *
 * @param {Array<Array>} optionGroups allOtherQuoteOptions (groups of options)
 * @param {object} selectedScreening state keyed by `${airline}_${agent}_${dest}`
 * @returns {Array} draft rows, one per priced row the operator has touched
 */
export function extractDraftRows(optionGroups, selectedScreening = {}) {
  const rows = [];
  for (const group of Array.isArray(optionGroups) ? optionGroups : []) {
    for (const option of Array.isArray(group) ? group : []) {
      for (const sp of Array.isArray(option?.screeningPrices)
        ? option.screeningPrices
        : []) {
        const key = draftRowKey(option, sp);
        if (!key) continue;
        const chosen = selectedScreening?.[key];
        const row = {
          key,
          screeningType: chosen?.type || "",
          profitMargin: num(sp.profitMargin),
          custom: num(sp.custom),
          extra: num(sp.extra),
          selected: !!sp.selectBox,
        };
        // Nothing set on this row — don't persist noise.
        const untouched =
          !row.screeningType &&
          !row.selected &&
          row.profitMargin === 0 &&
          row.custom === 0 &&
          row.extra === 0;
        if (untouched) continue;
        rows.push(row);
      }
    }
  }
  return rows;
}

/**
 * Re-apply a saved draft onto freshly built options, in place.
 *
 * Returns the screening selections so the caller can restore that state too.
 * Rows in the draft that no longer exist (a rate was deleted) are ignored
 * rather than recreated — the current rates decide what rows exist.
 *
 * @returns {object} selectedScreening map to restore
 */
export function applyDraftRows(optionGroups, rows) {
  const byKey = new Map();
  for (const r of Array.isArray(rows) ? rows : []) {
    if (r?.key) byKey.set(r.key, r);
  }
  if (byKey.size === 0) return {};

  const restoredScreening = {};

  for (const group of Array.isArray(optionGroups) ? optionGroups : []) {
    for (const option of Array.isArray(group) ? group : []) {
      for (const sp of Array.isArray(option?.screeningPrices)
        ? option.screeningPrices
        : []) {
        const key = draftRowKey(option, sp);
        const row = key && byKey.get(key);
        if (!row) continue;

        sp.profitMargin = num(row.profitMargin);
        sp.custom = num(row.custom);
        sp.extra = num(row.extra);
        sp.selectBox = !!row.selected;

        if (row.screeningType) {
          // The value is recomputed from the CURRENT rates, not restored from
          // the draft — a saved amount would re-apply a price we may no longer
          // offer. Only the operator's CHOICE of screening is theirs to keep.
          restoredScreening[key] = {
            type: row.screeningType,
            value: screeningValueFor(option, sp, row.screeningType),
          };
        }
      }
    }
  }

  return restoredScreening;
}
